import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Vehicule } from '../../../../shared/types/game';
import { VehiculeConfig, FonctionArmement, Competence, PersonnelConfig } from '../../../../shared/types/config';

interface VehiculeDetailsModalProps {
  show: boolean;
  vehicule: Vehicule | null;
  vehiculeConfigDetails: VehiculeConfig | null;
  fonctionsArmement: FonctionArmement[];
  competences: Competence[];
  /** Grades (config_personnel) pour libellés des priorités d’armement */
  personnelGradeConfigs?: PersonnelConfig[];
  loadingDetails: boolean;
  onClose: () => void;
  onSaveNom: (nom: string) => Promise<void>;
  onDeleteVehicule?: (vehiculeId: number) => Promise<void>;
}

const VehiculeDetailsModal: React.FC<VehiculeDetailsModalProps> = ({
  show,
  vehicule,
  vehiculeConfigDetails,
  fonctionsArmement,
  competences,
  personnelGradeConfigs = [],
  loadingDetails,
  onClose,
  onSaveNom,
  onDeleteVehicule,
}) => {
  const [editingNom, setEditingNom] = useState(false);
  const [nomValue, setNomValue] = useState(vehicule?.nom || '');
  const [savingNom, setSavingNom] = useState(false);
  const [deletingVehicule, setDeletingVehicule] = useState(false);
  const [previewFonctionArmement, setPreviewFonctionArmement] = useState<number | null>(null);

  // Réinitialiser le nom quand le véhicule change
  React.useEffect(() => {
    if (vehicule) {
      setNomValue(vehicule.nom);
      setEditingNom(false);
      setPreviewFonctionArmement(null);
    }
  }, [vehicule]);

  const handleSaveNom = async () => {
    if (!vehicule || !nomValue.trim() || nomValue.trim() === vehicule.nom) {
      setEditingNom(false);
      return;
    }
    setSavingNom(true);
    try {
      await onSaveNom(nomValue.trim());
      setEditingNom(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du nom:', error);
    } finally {
      setSavingNom(false);
    }
  };

  const handleCancelEditNom = () => {
    if (vehicule) {
      setNomValue(vehicule.nom);
    }
    setEditingNom(false);
  };

  const handleDeleteVehicule = async () => {
    if (!vehicule || !onDeleteVehicule || deletingVehicule) return;
    const confirmed = window.confirm(`Supprimer ${vehicule.nom} du bâtiment ?`);
    if (!confirmed) return;
    setDeletingVehicule(true);
    try {
      await onDeleteVehicule(vehicule.id);
      handleClose();
    } finally {
      setDeletingVehicule(false);
    }
  };

  if (!show || !vehicule) return null;

  const handleClose = () => {
    setPreviewFonctionArmement(null);
    onClose();
  };

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4" 
      onClick={handleClose}
    >
      <div 
        className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden border border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-800/50">
          <div className="flex-1">
            {editingNom ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={nomValue}
                  onChange={(e) => setNomValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveNom();
                    } else if (e.key === 'Escape') {
                      handleCancelEditNom();
                    }
                  }}
                  className="text-2xl font-bold text-white bg-gray-700 border border-blue-500 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                  disabled={savingNom}
                />
                <button
                  onClick={handleSaveNom}
                  disabled={savingNom || !nomValue.trim()}
                  className="text-green-400 hover:text-green-300 transition-colors p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Sauvegarder"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button
                  onClick={handleCancelEditNom}
                  disabled={savingNom}
                  className="text-red-400 hover:text-red-300 transition-colors p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Annuler"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-white">{vehicule.nom}</h2>
                <button
                  onClick={() => setEditingNom(true)}
                  className="text-gray-400 hover:text-white transition-colors p-1"
                  title="Modifier le nom"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                {onDeleteVehicule && (
                  <button
                    onClick={handleDeleteVehicule}
                    disabled={deletingVehicule}
                    className="text-red-400 hover:text-red-300 transition-colors p-1 disabled:opacity-50"
                    title="Supprimer ce véhicule"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            )}
            <p className="text-blue-400 text-sm mt-1">{vehicule.type}</p>
          </div>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Contenu */}
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-120px)]">
          {loadingDetails ? (
            <div className="text-center py-12 text-gray-400">
              Chargement des détails...
            </div>
          ) : (
            <>
              {/* Informations générales */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">Informations générales</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-800 rounded-lg p-4">
                    <p className="text-sm text-gray-400 mb-1">Statut</p>
                    <span className={`inline-block px-3 py-1 rounded text-sm font-medium ${
                      vehicule.statut === 'disponible'
                        ? 'bg-green-500/20 text-green-400'
                        : vehicule.statut === 'en_mission'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : vehicule.statut === 'en_maintenance'
                        ? 'bg-orange-500/20 text-orange-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {vehicule.statut === 'disponible' ? 'Disponible' :
                       vehicule.statut === 'en_mission' ? 'En mission' :
                       vehicule.statut === 'en_maintenance' ? 'En maintenance' : 'Panne'}
                    </span>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-4">
                    <p className="text-sm text-gray-400 mb-1">Kilométrage</p>
                    <p className="text-white font-semibold">{vehicule.kilometrage.toLocaleString()} km</p>
                  </div>
                  {vehicule.capacite && (
                    <div className="bg-gray-800 rounded-lg p-4">
                      <p className="text-sm text-gray-400 mb-1">Capacité</p>
                      <p className="text-white font-semibold">{vehicule.capacite} personnes</p>
                    </div>
                  )}
                  {vehicule.cout !== undefined && (
                    <div className="bg-gray-800 rounded-lg p-4">
                      <p className="text-sm text-gray-400 mb-1">Coût d'achat</p>
                      <p className="text-white font-semibold">{vehicule.cout.toLocaleString()} €</p>
                    </div>
                  )}
                  {vehicule.created_at && (
                    <div className="bg-gray-800 rounded-lg p-4">
                      <p className="text-sm text-gray-400 mb-1">Date de création</p>
                      <p className="text-white text-sm">{new Date(vehicule.created_at).toLocaleDateString('fr-FR')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Armement du type (config_vehicle / config_vehicle_competence / config_vehicle_grade) */}
              {vehiculeConfigDetails && (() => {
                const idsComp = vehiculeConfigDetails.id_competences_armement ?? [];
                const idsGrades = vehiculeConfigDetails.id_grades_priorite ?? [];
                const hasTypeArmement =
                  !!vehiculeConfigDetails.fonction_depart?.trim() ||
                  idsComp.length > 0 ||
                  idsGrades.length > 0 ||
                  vehiculeConfigDetails.specialiste;

                if (!hasTypeArmement) return null;

                return (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Armement (type d&apos;engin)</h3>

                    {vehiculeConfigDetails.fonction_depart?.trim() && (
                      <div className="bg-gradient-to-r from-blue-900/40 to-gray-800 rounded-lg p-4 border border-blue-500/30 mb-4">
                        <p className="text-xs text-blue-300/90 uppercase tracking-wide mb-1">Fonction au départ</p>
                        <p className="text-white font-semibold text-lg">{vehiculeConfigDetails.fonction_depart}</p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                      {vehiculeConfigDetails.specialiste && (
                        <span className="px-2 py-1 rounded text-xs bg-amber-500/20 text-amber-300 border border-amber-500/40">
                          Véhicule spécialiste
                        </span>
                      )}
                    </div>

                    {idsComp.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-300 mb-2">
                          Compétences requises pour armer ({idsComp.length})
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {idsComp.map((cid) => {
                            const c = competences.find((x) => x.id === cid);
                            return (
                              <div
                                key={cid}
                                className="bg-gray-800 rounded-lg p-3 border border-blue-500/25"
                              >
                                <p className="text-white font-medium">{c?.nom ?? `Compétence #${cid}`}</p>
                                {c?.description && (
                                  <p className="text-gray-500 text-xs mt-1 line-clamp-2">{c.description}</p>
                                )}
                                <span className="text-[10px] text-gray-600 mt-1 inline-block">ID {cid}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {idsGrades.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-300 mb-2">
                          Priorité des grades sur l&apos;engin
                        </h4>
                        <ol className="list-decimal list-inside space-y-2">
                          {idsGrades.map((gid, idx) => {
                            const g = personnelGradeConfigs.find((p) => p.id === gid);
                            return (
                              <li key={`${gid}-${idx}`} className="text-gray-200">
                                <span className="font-medium text-white">
                                  {g?.grade ?? `Grade #${gid}`}
                                </span>
                                {g?.grade_code && (
                                  <span className="text-gray-500 text-sm ml-2">({g.grade_code})</span>
                                )}
                                <span className="text-[10px] text-gray-600 ml-2">config_personnel #{gid}</span>
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Snapshot instance (player_vehicle.id_competences) */}
              {!loadingDetails &&
                vehicule.id_competences &&
                vehicule.id_competences.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-white mb-3">
                      Compétences enregistrées sur cet engin (instance)
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {vehicule.id_competences.map((cid) => {
                        const c = competences.find((x) => x.id === cid);
                        return (
                          <div
                            key={cid}
                            className="bg-gray-800 rounded-lg p-3 border border-purple-500/30"
                          >
                            <p className="text-white font-medium">{c?.nom ?? `Compétence #${cid}`}</p>
                            <span className="text-[10px] text-gray-600">ID {cid}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              {/* Fonctions d'armement */}
              {vehiculeConfigDetails && vehiculeConfigDetails.id_fonctions_armement && vehiculeConfigDetails.id_fonctions_armement.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    Fonctions d'armement disponibles ({vehiculeConfigDetails.id_fonctions_armement.length})
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    {vehiculeConfigDetails.id_fonctions_armement.map((fonctionId) => {
                      const fonction = fonctionsArmement.find(f => f.id === fonctionId);
                      const isActive = vehicule.id_fonction_armement === fonctionId;
                      const isPreview = previewFonctionArmement === fonctionId;
                      
                      return fonction ? (
                        <button
                          key={fonctionId}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPreviewFonctionArmement(isPreview ? null : Number(fonctionId));
                          }}
                          className={`bg-gray-800 rounded-lg p-4 border text-left transition-all cursor-pointer ${
                            isActive 
                              ? 'border-blue-500 bg-blue-500/10' 
                              : isPreview
                              ? 'border-green-500 bg-green-500/10 hover:border-green-400'
                              : 'border-gray-700 hover:border-gray-600'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-white font-semibold">{fonction.nom}</p>
                                {isActive && (
                                  <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                                    Active
                                  </span>
                                )}
                                {isPreview && !isActive && (
                                  <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                                    Prévisualisation
                                  </span>
                                )}
                              </div>
                              {fonction.description && (
                                <p className="text-gray-400 text-sm mt-1">{fonction.description}</p>
                              )}
                            </div>
                          </div>
                        </button>
                      ) : (
                        <button
                          key={fonctionId}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPreviewFonctionArmement(isPreview ? null : Number(fonctionId));
                          }}
                          className={`bg-gray-800 rounded-lg p-4 border text-left transition-all cursor-pointer ${
                            isActive 
                              ? 'border-blue-500 bg-blue-500/10' 
                              : isPreview
                              ? 'border-green-500 bg-green-500/10 hover:border-green-400'
                              : 'border-gray-700 hover:border-gray-600'
                          }`}
                        >
                          <p className="text-gray-400">Fonction d'armement #{fonctionId}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Fonction d'armement active (fallback si pas de config) */}
              {(!vehiculeConfigDetails || !vehiculeConfigDetails.id_fonctions_armement || vehiculeConfigDetails.id_fonctions_armement.length === 0) && vehicule.id_fonction_armement && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Fonction d'armement active</h3>
                  <div className="bg-gray-800 rounded-lg p-4 border border-blue-500 bg-blue-500/10">
                    {(() => {
                      const fonction = fonctionsArmement.find(f => f.id === vehicule.id_fonction_armement);
                      return fonction ? (
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-semibold text-lg">{fonction.nom}</p>
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                              Active
                            </span>
                          </div>
                          {fonction.description && (
                            <p className="text-gray-400 text-sm mt-2">{fonction.description}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-400">Fonction d'armement #{vehicule.id_fonction_armement}</p>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Compétences */}
              {(() => {
                // Si une fonction est en prévisualisation, utiliser uniquement ses compétences
                // Sinon, utiliser la fonction active du véhicule
                const fonctionIdPourAffichage = previewFonctionArmement ?? vehicule.id_fonction_armement;
                const fonction = fonctionIdPourAffichage 
                  ? fonctionsArmement.find(f => f.id === fonctionIdPourAffichage)
                  : null;
                
                // Si aucune fonction n'est trouvée, ne rien afficher
                if (!fonction || !fonction.id_competences || fonction.id_competences.length === 0) {
                  return null;
                }
                
                // Utiliser directement les compétences de la fonction avec leurs propriétés (ordre, optionnel, ref)
                // Créer un tableau avec toutes les instances de compétences (car une compétence peut apparaître plusieurs fois)
                const competencesWithOrder = fonction.id_competences
                  .map((compFonction, index) => {
                    const competence = competences.find(c => c.id === compFonction.id);
                    if (!competence) return null;
                    
                    return {
                      id: compFonction.id,
                      competence,
                      ordre: compFonction.ordre || 999,
                      isOptionnel: compFonction.optionnel === true,
                      isRef: compFonction.ref === true,
                      // Utiliser un index unique pour la clé car une compétence peut apparaître plusieurs fois
                      uniqueKey: `${compFonction.id}-${index}-${compFonction.ordre}`
                    };
                  })
                  .filter((item): item is { id: number; competence: Competence; ordre: number; isOptionnel: boolean; isRef: boolean; uniqueKey: string } => item !== null)
                  .sort((a, b) => {
                    // Trier par ordre, puis par index si même ordre
                    if (a.ordre !== b.ordre) {
                      return a.ordre - b.ordre;
                    }
                    return 0;
                  });
                
                if (competencesWithOrder.length === 0) {
                  return null;
                }
                
                return (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-white mb-4">
                      {previewFonctionArmement 
                        ? `Armement - ${fonction.nom}` 
                        : 'Compétences requises'} ({competencesWithOrder.length})
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {competencesWithOrder.map(({ uniqueKey, competence, isOptionnel, isRef }) => {
                        const hasBoth = isOptionnel && isRef;
                        return (
                          <div 
                            key={uniqueKey} 
                            className={`bg-gray-800 rounded-lg p-4 border-2 ${
                              hasBoth
                                ? 'border-l-4 border-l-green-500/70 border-r border-r-yellow-500/50 border-t border-t-yellow-500/50 border-b border-b-yellow-500/50'
                                : isRef 
                                ? 'border-green-500/20' 
                                : isOptionnel 
                                ? 'border-yellow-500/20' 
                                : 'border-blue-500/20'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className={`font-medium ${
                                hasBoth
                                  ? 'text-green-300'
                                  : isRef 
                                  ? 'text-green-400' 
                                  : isOptionnel 
                                  ? 'text-yellow-400' 
                                  : 'text-blue-400'
                              }`}>
                                {competence.nom}
                              </span>
                              <div className="flex items-center gap-2">
                                {isOptionnel && (
                                  <span className="text-xs text-yellow-400 bg-yellow-500/20 px-2 py-1 rounded">
                                    Optionnel
                                  </span>
                                )}
                                {isRef && (
                                  <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded">
                                    Ref
                                  </span>
                                )}
                              </div>
                            </div>
                            {competence.is_specialite && (
                              <span className="text-xs text-purple-400 mt-2 inline-block">Spécialité</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const fonctionIdPourAffichage = previewFonctionArmement ?? vehicule.id_fonction_armement;
                const fonction = fonctionIdPourAffichage 
                  ? fonctionsArmement.find(f => f.id === fonctionIdPourAffichage)
                  : null;
                const competencesAAfficher = fonction?.id_competences 
                  ? fonction.id_competences.map(c => c.id)
                  : [];
                const aDesFonctionsConfig =
                  (!!vehiculeConfigDetails?.id_fonctions_armement &&
                    vehiculeConfigDetails.id_fonctions_armement.length > 0) ||
                  (!!vehiculeConfigDetails?.id_competences_armement &&
                    vehiculeConfigDetails.id_competences_armement.length > 0) ||
                  !!vehiculeConfigDetails?.fonction_depart?.trim();
                const showEmpty =
                  !fonction &&
                  competencesAAfficher.length === 0 &&
                  !vehicule.id_fonction_armement &&
                  !aDesFonctionsConfig;

                return showEmpty ? (
                  <div className="mb-6">
                    <div className="bg-gray-800 rounded-lg p-4 text-center text-gray-400">
                      <p>Aucune fonction d'armement ou compétence assignée</p>
                    </div>
                  </div>
                ) : null;
              })()}

              <div className="border-t border-gray-700 pt-4 mt-2">
                <h3 className="text-lg font-semibold text-white mb-4">Informations système</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">ID véhicule</p>
                    <p className="text-white">{String(vehicule.id)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">ID configuration (type)</p>
                    <p className="text-white">
                      {vehicule.config_vehicle_id != null ? vehicule.config_vehicle_id : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">ID bâtiment</p>
                    <p className="text-white">
                      {vehicule.batiment_id != null ? String(vehicule.batiment_id) : 'Non assigné'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Statut (BDD)</p>
                    <p className="text-white">
                      {vehicule.id_status ?? vehicule.vehicle_status_id ?? '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">ID fonction armement (instance)</p>
                    <p className="text-white">
                      {vehicule.id_fonction_armement != null ? vehicule.id_fonction_armement : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400">Utilisateur (ID)</p>
                    <p className="text-white">{vehicule.user_id ?? '—'}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default VehiculeDetailsModal;

